/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Audience dashboards", description = "Track audience size, activity, and sync health, and customize the default view with metrics and dashboards from Intelligence.", lastUpdatedDate = new Date(1787788800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Audience dashboards",
        description: "Track audience size, activity, and sync health, and customize the default view with metrics and dashboards from Intelligence.",
        lastUpdatedDate: "Thu Aug 27 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What the default dashboard shows",
          "depth": 0,
          "id": "what-the-default-dashboard-shows"
        }, {
          "value": "Add metrics to the default dashboard",
          "depth": 0,
          "id": "add-metrics-to-the-default-dashboard"
        }, {
          "value": "Use a custom dashboard",
          "depth": 0,
          "id": "use-a-custom-dashboard"
        }, {
          "value": "Dashboard behavior",
          "depth": 0,
          "id": "dashboard-behavior"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      a: "a",
      h2: "h2",
      ul: "ul",
      li: "li",
      img: "img",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: "A saved audience. (Optional) At least one dashboard"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Every saved audience includes a default dashboard for tracking audience size, activity, and sync health over time. You can add metrics from Intelligence to the default dashboard or replace it with a custom Intelligence dashboard."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You'll find an audience's dashboard on its ", _jsx(_components.strong, {
          children: "Overview"
        }), " tab. It tracks how the audience changes after you save and sync it. While you're building an audience, you can ", _jsx(_components.a, {
          href: "/customer-studio/usage#preview-and-validate-the-audience",
          children: "preview its members"
        }), " and open ", _jsx(_components.a, {
          href: "/customer-studio/insights",
          children: "Insights"
        }), " to check its size and composition first."]
      }), "\n", _jsx(_components.h2, {
        id: "what-the-default-dashboard-shows",
        children: "What the default dashboard shows"
      }), "\n", _jsx(_components.p, {
        children: "The default dashboard includes these charts:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Audience size"
          }), " — The current number of audience members."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Audience size over time"
          }), " — How membership changes over time."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User activity"
          }), " — Events from members of the audience."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync size over time"
          }), " — Completed syncs over the past seven days."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/audience-overviews/default-dashboard.png",
          alt: "Default dashboard"
        })
      }), "\n", _jsx(_components.p, {
        children: "Change the time range to view the last 7, 30, 60, or 90 days, or a custom range. Refresh the page to load the latest chart data."
      }), "\n", _jsx(_components.h2, {
        id: "add-metrics-to-the-default-dashboard",
        children: "Add metrics to the default dashboard"
      }), "\n", _jsxs(_components.p, {
        children: ["You can add saved ", _jsx(_components.a, {
          href: "/campaign-intelligence/metrics",
          children: "metrics"
        }), " from Intelligence to the default dashboard as KPIs, such as total revenue or order count."]
      }), "\n", _jsxs(_components.p, {
        children: ["Metrics are created in ", _jsx(_components.strong, {
          children: "Intelligence > Metrics"
        }), " and built on a parent model."]
      }), "\n", _jsx(_components.p, {
        children: "To add a metric to an audience:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open the audience's ", _jsx(_components.strong, {
            children: "Settings"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Primary metrics"
          }), ", select one or more saved metrics."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/audience-overviews/primary-metrics.png",
          alt: "Select a primary metric in audience settings"
        })
      }), "\n", _jsx(_components.p, {
        children: "Primary metrics appear at the bottom of the default dashboard."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/audience-overviews/saved-metric-default-dashboard-highlight.png",
          alt: "The Total revenue metric shown as a KPI at the bottom of the default dashboard"
        })
      }), "\n", _jsx(_components.p, {
        children: "Primary metrics appear only on the default dashboard. A custom dashboard replaces the default view, so primary metrics don't appear on it."
      }), "\n", _jsx(_components.h2, {
        id: "use-a-custom-dashboard",
        children: "Use a custom dashboard"
      }), "\n", _jsxs(_components.p, {
        children: ["You can replace the default dashboard with a custom ", _jsx(_components.a, {
          href: "/campaign-intelligence/dashboards",
          children: "dashboard"
        }), " created in Intelligence. Custom dashboards are created in ", _jsx(_components.strong, {
          children: "Intelligence > Dashboards"
        }), " and can contain Insights and funnel charts."]
      }), "\n", _jsx(_components.p, {
        children: "To use a custom dashboard for an audience:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open the audience's ", _jsx(_components.strong, {
            children: "Settings"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Overview dashboard"
          }), ", select an existing dashboard."]
        }), "\n", _jsxs(_components.li, {
          children: ["To create a dashboard, select ", _jsx(_components.strong, {
            children: "Create a new dashboard"
          }), " and configure it in Intelligence."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/audience-overviews/audience-settings.png",
          alt: "Audience settings"
        })
      }), "\n", _jsx(_components.p, {
        children: "On an audience, the dashboard shows only that audience's members, so you can reuse the same dashboard across audiences that share a parent model. Global dashboard filters don't appear on an audience, and the original dashboard keeps its configured filters."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/audience-overviews/audience-custom-dashboard.png",
          alt: "Custom dashboard in an audience"
        })
      }), "\n", _jsx(_components.h2, {
        id: "dashboard-behavior",
        children: "Dashboard behavior"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sampling"
          }), " — If the audience uses ", _jsx(_components.a, {
            href: "/customer-studio/sampling",
            children: "sampling"
          }), ", dashboard results reflect those settings."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Caching"
          }), " — Audience dashboards use the same caching behavior as Intelligence dashboards."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Permissions"
          }), " — Anyone who can view an audience's members can view its dashboard. Only editors can change the dashboard configured for the audience."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
